import { FormEvent, useState } from "react";
import { AlertTriangle, CalendarCheck } from "lucide-react";
import { Modal } from "../Modal";
import { formatSchoolDate } from "../students/studentDisplay";
import { apiFieldErrors } from "../../utils/apiError";
import type { Attendance, LeaveRequest, StaffProfile } from "../../services/staffService";
import { ATTENDANCE_STATUSES, hoursBetween, shortTime } from "../../pages/attendanceFilters";
import { localToday } from "../../pages/leaveFilters";

export type AttendanceValues = {
  staff: number;
  date: string;
  status: string;
  check_in_time?: string;
  check_out_time?: string;
  hours_worked: string;
  notes: string;
};

const FORM_ID = "record-attendance-form";
// Nobody clocks in when absent or on leave, so no times are asked for.
const WITHOUT_TIMES = ["absent", "leave"];

export default function RecordAttendanceModal({
  profiles,
  attendance,
  leaves,
  isSaving,
  error,
  onSubmit,
  onClose,
}: {
  profiles: StaffProfile[];
  attendance: Attendance[];
  leaves: LeaveRequest[];
  isSaving: boolean;
  error: unknown;
  onSubmit: (values: AttendanceValues) => void;
  onClose: () => void;
}) {
  const [staff, setStaff] = useState("");
  const [date, setDate] = useState(localToday());
  const [status, setStatus] = useState("present");
  const [checkIn, setCheckIn] = useState("");
  const [checkOut, setCheckOut] = useState("");
  const [hoursOverride, setHoursOverride] = useState("");
  const [notes, setNotes] = useState("");
  const { fields: errors, message } = apiFieldErrors(
    error,
    ["staff", "date", "status", "check_in_time", "check_out_time", "hours_worked", "notes"],
    "Attendance could not be recorded. Please try again.",
  );

  const staffId = Number(staff);
  const working = profiles.filter((profile) =>
    ["active", "probation", "suspended"].includes(profile.employment_status),
  );
  const existing = attendance.find((record) => record.staff === staffId && record.date === date);
  const onLeave = leaves.find(
    (leave) =>
      leave.staff === staffId && leave.status === "approved" && leave.start_date <= date && leave.end_date >= date,
  );
  const needsTimes = !WITHOUT_TIMES.includes(status);
  const computedHours = needsTimes ? hoursBetween(checkIn, checkOut) : null;
  const hours = hoursOverride || (computedHours !== null ? String(computedHours) : "0");
  const timesBackwards = Boolean(checkIn && checkOut && checkOut <= checkIn);

  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    onSubmit({
      staff: staffId,
      date,
      status,
      check_in_time: needsTimes && checkIn ? checkIn : undefined,
      check_out_time: needsTimes && checkOut ? checkOut : undefined,
      hours_worked: needsTimes ? hours : "0",
      notes: notes.trim(),
    });
  };

  const fieldError = (name: string) =>
    errors[name] && (
      <span role="alert" className="mt-1 block text-xs font-normal text-red-700">
        {errors[name]}
      </span>
    );

  return (
    <Modal
      isOpen
      size="2xl"
      title="Record attendance"
      onClose={() => {
        if (!isSaving) onClose();
      }}
      footer={
        <>
          <button type="button" className="btn btn-outline" disabled={isSaving} onClick={onClose}>
            Cancel
          </button>
          <button
            type="submit"
            form={FORM_ID}
            disabled={isSaving || Boolean(existing) || timesBackwards}
            className="btn btn-primary"
          >
            {isSaving ? "Saving…" : "Record attendance"}
          </button>
        </>
      }
    >
      <form id={FORM_ID} onSubmit={submit} className="font-refined space-y-5">
        {message && (
          <p role="alert" className="rounded-md border border-red-200 bg-red-50 px-3 py-2.5 text-sm text-red-800">
            {message}
          </p>
        )}

        <div className="grid gap-4 sm:grid-cols-[1fr_12rem]">
          <label className="block text-sm font-medium text-gray-700">
            Staff member *
            <select required value={staff} onChange={(event) => setStaff(event.target.value)} className="input mt-1">
              <option value="">Choose a staff member</option>
              {working.map((profile) => (
                <option key={profile.id} value={profile.id}>
                  {profile.user_name} · {profile.position_title}
                </option>
              ))}
            </select>
            {fieldError("staff")}
          </label>
          <label className="block text-sm font-medium text-gray-700">
            Date *
            <input
              required
              type="date"
              value={date}
              max={localToday()}
              onChange={(event) => setDate(event.target.value)}
              className="input mt-1"
            />
            {fieldError("date")}
          </label>
        </div>

        {existing && (
          <div role="alert" className="flex gap-2 rounded-md border border-red-200 bg-red-50 px-3 py-2.5 text-sm text-red-800">
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
            <p>
              Already recorded for {formatSchoolDate(date)} as{" "}
              <strong>{ATTENDANCE_STATUSES.find(([value]) => value === existing.status)?.[1] ?? existing.status}</strong>
              {existing.check_in_time && ` (${shortTime(existing.check_in_time)}–${shortTime(existing.check_out_time) || "?"})`}.
              Each person has one record a day.
            </p>
          </div>
        )}

        {onLeave && !existing && (
          <div className="flex flex-wrap items-center justify-between gap-2 rounded-md border border-primary-200 bg-primary-50 px-3 py-2.5 text-sm text-primary-900">
            <span className="flex items-center gap-2">
              <CalendarCheck className="h-4 w-4 shrink-0 text-primary-600" />
              On approved {onLeave.leave_type_name} from {formatSchoolDate(onLeave.start_date)} to{" "}
              {formatSchoolDate(onLeave.end_date)}.
            </span>
            {status !== "leave" && (
              <button type="button" onClick={() => setStatus("leave")} className="font-medium underline">
                Mark as on leave
              </button>
            )}
          </div>
        )}

        <fieldset>
          <legend className="text-sm font-medium text-gray-700">Status *</legend>
          <div className="mt-1.5 grid grid-cols-2 gap-2 sm:grid-cols-5">
            {ATTENDANCE_STATUSES.map(([value, label]) => (
              <label
                key={value}
                className={`cursor-pointer rounded-lg border px-3 py-2 text-center text-sm font-medium transition-colors ${
                  status === value
                    ? "border-primary-500 bg-primary-50 text-primary-800 ring-1 ring-primary-500"
                    : "border-gray-200 text-gray-700 hover:border-gray-300"
                }`}
              >
                <input
                  type="radio"
                  name="status"
                  value={value}
                  checked={status === value}
                  onChange={() => setStatus(value)}
                  className="sr-only"
                />
                {label}
              </label>
            ))}
          </div>
          <p className="mt-1 text-xs text-gray-500">
            Approved leave is an entitlement and never counts as an absence on an appraisal.
          </p>
        </fieldset>

        {needsTimes && (
          <div className="grid gap-4 sm:grid-cols-3">
            <label className="block text-sm font-medium text-gray-700">
              Checked in
              <input type="time" value={checkIn} onChange={(event) => setCheckIn(event.target.value)} className="input mt-1" />
              {fieldError("check_in_time")}
            </label>
            <label className="block text-sm font-medium text-gray-700">
              Checked out
              <input type="time" value={checkOut} onChange={(event) => setCheckOut(event.target.value)} className="input mt-1" />
              {timesBackwards ? (
                <span role="alert" className="mt-1 block text-xs font-normal text-red-700">
                  Must be after check-in.
                </span>
              ) : (
                fieldError("check_out_time")
              )}
            </label>
            <label className="block text-sm font-medium text-gray-700">
              Hours worked
              <input
                type="number"
                min="0"
                max="24"
                step="0.25"
                value={hours}
                onChange={(event) => setHoursOverride(event.target.value)}
                className="input mt-1 tabular-nums"
              />
              <span className="mt-1 block text-xs font-normal text-gray-500">
                {computedHours !== null && !hoursOverride ? "Worked out from the times." : "Used to spot early departures."}
              </span>
            </label>
          </div>
        )}

        <label className="block text-sm font-medium text-gray-700">
          Notes
          <textarea
            rows={2}
            value={notes}
            onChange={(event) => setNotes(event.target.value)}
            className="input mt-1 h-auto"
            placeholder="e.g. Left at 1pm for a hospital appointment."
          />
        </label>
      </form>
    </Modal>
  );
}
